import { Col, Divider, Form, Row, Table } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";

import NormalTag from "../../../../components/tags/NormalTag";
import { Colors } from "../../../../constants/Colors";
import { WarrantyInfoResponse } from "../../../../graphql/operations/@types";
import { setFeatureMarketLanguage } from "../../../../i18n/featureLanguageSession"
import { MarketType, NormalTagTypes } from "../../../../types/types";
import { createPrintColumns } from "../content/warrantyColumns";
import { StatusTypes } from "../content/warrantyContent";
import "./WarrantyPrint.scss";

interface WarrantyPrintProps {
  warrantyInfoList: WarrantyInfoResponse[];
  market: MarketType;
}

const textStyle: React.CSSProperties = {
  color: Colors.notEditableText,
  lineHeight: "22px",
  whiteSpace: "pre-wrap",
  wordBreak: "break-word",
  margin: 0,
  padding: '4px 0',
};

const WarrantyPrint = ({
  warrantyInfoList,
  market,
}: WarrantyPrintProps) => {
  setFeatureMarketLanguage("warranty", market);

  const [form] = Form.useForm();
  const { t: trans } = useTranslation(["clubApp", "common"]);

  const statusTagMap: Record<StatusTypes, JSX.Element> = {
    [StatusTypes.Awaiting]: (
      <NormalTag type={NormalTagTypes.AWAITING} />
    ),
    [StatusTypes.Complete]: (
      <NormalTag type={NormalTagTypes.COMPLETE} />
    ),
    [StatusTypes.Canceled]: (
      <NormalTag type={NormalTagTypes.CANCELED} />
    ),
  };

  return (
    <div id="warrantyPrintWrapper">
      {warrantyInfoList.map((warrantyInfo) => (
        <div key={warrantyInfo.applicationId} className="print-page">
          <Form
            form={form}
            labelCol={{ span: 11 }}
            wrapperCol={{ span: 13 }}
          >
            <img
              src={require("../../../../assets/images/logo-light.png")}
              className="logo"
              alt=""
            />
            <Divider />
            <Row gutter={3}>
              <Col span={8}>
                <Form.Item label={trans("clubApp:warranty.applicationNo")}>
                  <p style={textStyle}>{warrantyInfo?.applicationNo}</p>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={trans("clubApp:warranty.applicationDate")}>
                  <p style={textStyle}>{warrantyInfo?.applicationDate ? dayjs(warrantyInfo?.applicationDate).format('YYYY/MM/DD HH:mm') : ""}</p>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={trans("clubApp:warranty.ACNO")}>
                  <p style={textStyle}>{warrantyInfo?.accountNo}</p>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={3}>
              <Col span={8}>
                <Form.Item label={trans("clubApp:warranty.itemQty")}>
                  <p style={textStyle}>{warrantyInfo?.itemQty}</p>
                </Form.Item>
              </Col>
              <Col span={8}>
                <Form.Item label={trans("common:common.status")}>
                  {statusTagMap[warrantyInfo?.status as StatusTypes]}
                </Form.Item>
              </Col>
              <Col span={8}>
                {warrantyInfo?.status !== StatusTypes.Awaiting &&
                  <Form.Item label={trans("clubApp:warranty.statusUpdatedDate")}>
                    <p style={textStyle}>{warrantyInfo?.updateDate ? dayjs(warrantyInfo?.updateDate).format('YYYY/MM/DD') : ""}</p>
                  </Form.Item>
                }
              </Col>
            </Row>
            <div className="table-title-container">
              <Form.Item noStyle>
                <Table
                  className="custom-table-style"
                  dataSource={[
                    ...(warrantyInfo?.otherProductList || []),
                    ...(warrantyInfo?.talkalongCardProductList || []),
                  ]}
                  columns={createPrintColumns(trans)}
                  pagination={false}
                  rowKey="categoryId"
                />
              </Form.Item>
            </div>
          </Form>
        </div>
      ))}
    </div>
  );
};

export default WarrantyPrint;
